import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../api/api";
import { setConfig } from "../reduxtoolkit/userconfigslice";
import { showToast } from "../reduxtoolkit/toastslice";
import { eventBus } from "../utils/eventBus";
import {
  busyReason,
  getNavigationState,
  getPendingNavigation,
  hasLocalRecording,
  reloadPage,
} from "../utils/deferredNavigation";
import { getPracticeId, practiceDepartments, practiceFeatures } from "../config/practice";
import "./workinglocation.css";

// The practice's locations (9928: 1 Odessa, 2 Frisco) -- config/practice.js.
export const LOCATIONS = practiceDepartments();

// Emitted after a switch that did NOT reload the page (a recording is
// running): { departmentid, chartOpen, localRecording }. App remounts the
// routed page on it when the chart is closed, so that page refetches for the
// new location -- unless a screen holds a local recording (Edit with Voice in
// Note Review, a voice message): the remount would unmount it mid-recording.
export const WORKING_LOCATION_CHANGED = "workingLocationChanged";

const locationName = (id) =>
  (LOCATIONS.find((l) => l.id === String(id)) || {}).name || "the other location";

const CONTINUES = {
  recording: "Your recording continues.",
  voice: "Your voice recording continues.",
  note: "Your note is still processing.",
};
const ONCE = { recording: "Once it ends", voice: "Once it ends", note: "Once it's done" };

// The toast after a switch that did not reload: says what actually happens
// next (utils/deferredNavigation). Nothing reloads by itself when the
// recording ends; the page refreshes when the chart is closed, or on the
// notice's Refresh button (or the next page change / return to the tab).
export const switchedDuringRecordingMessage = (id) => {
  const busy = busyReason();
  const { chartOpen } = getNavigationState();
  const queued = getPendingNavigation();
  const head = `Switched to ${locationName(id)}.${busy ? ` ${CONTINUES[busy]}` : ""}`;
  if (queued && queued.kind === "redirect") {
    return busy
      ? `${head} Your sign-in has expired: ${ONCE[busy].toLowerCase()}, sign in again to finish.`
      : `${head} Your sign-in has expired: sign in again to finish.`;
  }
  if (chartOpen) {
    return busy
      ? `${head} ${ONCE[busy]}, closing the chart refreshes the page.`
      : `${head} The page will refresh when you close the chart.`;
  }
  return `${head} ${busy ? ONCE[busy] : "Then"}, click Refresh at the bottom of the page (or open another page) to finish.`;
};

// Working-location switcher (Odessa/Frisco). The server reads the user's
// config per request, so after the switch every location-scoped view follows.
// With no recording running the page reloads, as before. During a recording a
// reload would end it (2026-09-30: a provider switched to read the other
// location's chat mid-visit and the recording stopped), so instead:
//   - the store's config changes at once (this select, and every view that
//     reads the location from it);
//   - the dashboard under an open chart refetches (refreshDashboard); with the
//     chart closed App remounts the routed page (WORKING_LOCATION_CHANGED);
//   - the header refetches its badges and reopens the chat inbox fresh
//     (onSwitchedInPlace);
//   - no remount at all while a screen holds a local recording (voice edit,
//     voice message): unmounting it would end that recording;
//   - the full reload waits in utils/deferredNavigation: once the recording
//     (and its note) is done it runs when the chart is closed, on the
//     notice's Refresh button, or at the next page change / tab return --
//     never at an arbitrary moment.
// The running recording keeps its own visit and department: both were fixed
// into its WebSocket query string when it started.
function WorkingLocationSwitcher({ onSwitchedInPlace, onOpenChange }) {
  const dispatch = useDispatch();
  const userConfig = useSelector((state) => state.userconfig);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const current = String(userConfig?.config?.departmentid || "1");

  // Tell the header while the menu is open: it raises itself above the chart
  // window only then, so the menu opens over it; closed, it stays below every
  // in-app popup (review 10-06 B2). Reports closed again on unmount.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    if (onOpenChangeRef.current) onOpenChangeRef.current(open);
  }, [open]);
  useEffect(() => () => {
    if (onOpenChangeRef.current) onOpenChangeRef.current(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleChange = async (e) => {
    setOpen(false);
    const dep = Number(e.target.value);
    if (!dep || String(dep) === current) return;
    setSaving(true);
    try {
      await api.put(
        `/v1/${getPracticeId()}/user/switch-department`,
        { departmentid: dep },
      );
    } catch (err) {
      console.error("Department switch failed", err);
      dispatch(
        showToast({
          message: "Could not switch location. Please try again.",
          severity: "error",
        }),
      );
      setSaving(false);
      return;
    }
    // The server stores departmentid as a string; keep the same shape.
    dispatch(
      setConfig({
        config: { ...(userConfig?.config || {}), departmentid: String(dep) },
      }),
    );
    const status = reloadPage({
      reason: "location",
      subject: `Location switched to ${locationName(dep)}`,
      verb: "Refresh to finish",
    });
    if (status === "deferred") {
      const { chartOpen } = getNavigationState();
      const localRecording = hasLocalRecording();
      if (chartOpen) eventBus.emit("refreshDashboard");
      eventBus.emit(WORKING_LOCATION_CHANGED, { departmentid: dep, chartOpen, localRecording });
      if (onSwitchedInPlace) onSwitchedInPlace(dep, { localRecording });
      dispatch(
        showToast({
          message: switchedDuringRecordingMessage(dep),
          severity: "info",
        }),
      );
    }
    setSaving(false);
  };

  // A pill switch in the header; clicking it opens the list of locations.
  return (
    <div className="bmloc" ref={boxRef}>
      <button
        type="button"
        className={`bmloc-btn${open ? " open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        title="Working location"
        aria-label="Working location"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg className="bmloc-pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 21s-7-6.1-7-11.5a7 7 0 0 1 14 0C19 14.9 12 21 12 21Z" />
          <circle cx="12" cy="9.5" r="2.5" />
        </svg>
        <span className="bmloc-name">{locationName(current)}</span>
        <svg className="bmloc-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="bmloc-menu" role="listbox" aria-label="Working location">
          <div className="bmloc-menu-cap">Switch location</div>
          {LOCATIONS.map((l) => {
            const on = l.id === current;
            return (
              <button
                type="button"
                key={l.id}
                role="option"
                aria-selected={on}
                className={`bmloc-opt${on ? " on" : ""}`}
                onClick={() => handleChange({ target: { value: l.id } })}
              >
                <span className="bmloc-opt-dot" />
                {l.name}
                {on && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="5 12.5 10 17 19 7.5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Only practice 9928 has more than one location; elsewhere (osa.brainymed.com
// = 7825, one department) there is nothing to switch, so nothing renders and
// no switch-department request can be made.
export default function WorkingLocationSelect(props) {
  if (!practiceFeatures().workingLocations) return null;
  return <WorkingLocationSwitcher {...props} />;
}
